import React, {
  FC,
  useMemo,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useContext,
  useCallback,
} from 'react'
import { Environment } from 'relay-runtime'
import { useLazyLoadQuery, graphql } from 'react-relay'

import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  Location,
  Router,
  useMatch,
} from 'react-router-dom'

import { lazy as loadable } from '@loadable/component'
import { ServerContext } from '@data/ServerContext'

import { TamaguiProvider } from 'tamagui'
import tamaguiConfig from '../config/tamagui.config'
import { ThemeContext } from '@newComponents/ThemeContext'

// import '@tamagui/core/reset.css'

import { I18nProvider } from '@data/i18n'
import { ErrorBoundary } from '@newComponents/ErrorBoundary'

import { DynamicIsland as DynamicIslandNew } from '@newComponents/DynamicIsland/DynamicIsland'
import { DynamicIslandProvider } from '@newComponents/DynamicIsland/DynamicIslandProvider'
import { Footer } from '@newComponents/Footer'
import { PaletteRootProvider } from '@newComponents/Palette'
import { SearchProvider } from '@newComponents/Search/SearchProvider'

import { Provider as DataProvider } from '@data/Provider'
import { wrapSuspenseComponent, wrapSuspenseScreen } from '@newComponents/SuspenseWrapper'

import * as Sentry from '@sentry/react'

const MAINTENANCE_MODE = false

import { useMeta, useTitle } from 'hoofd'

const HomeScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/HomeScreen/HomeScreen'))
)

const WrappedActorNavigator = wrapSuspenseScreen(
  loadable(() => import('./navigators/ActorNavigator'))
)
const WrappedEventNavigator = wrapSuspenseScreen(
  loadable(() => import('./navigators/EventNavigator'))
)

const GuildsScreenNew = wrapSuspenseScreen(
  loadable(() => import('./screens/GuildsScreen/GuildsScreenNew'))
)

const GuildNewScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/GuildNewScreen/GuildNewScreen'))
)

const GuildNewContent = wrapSuspenseScreen(
  loadable(() => import('./screens/GuildNewScreen/GuildNewContent'))
)

const EventsScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/EventsScreen/EventsScreen'))
)

const PresentationsScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/PresentationsScreen/PresentationsScreen'))
)

const MeetupIntegrationScreen = wrapSuspenseScreen(
  loadable(
    () => import('./screens/MeetupIntegrationScreen/MeetupIntegrationScreen')
  )
)

const BlueskyIntegrationScreen = wrapSuspenseScreen(
  loadable(
    () => import('./screens/BlueskyIntegrationScreen/BlueskyIntegrationScreen')
  )
)

const GoogleCalendarConnectScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import(
        './screens/GoogleCalendarConnectScreen/GoogleCalendarConnectScreen'
      )
  )
)

const OAuthAuthorizeScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import('./screens/OAuthAuthorizeScreen/OAuthAuthorizeScreen')
  )
)

const PresentationScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/PresentationScreen/PresentationScreen'))
)

const PresentationScreenContentWrapper = wrapSuspenseComponent(
  loadable(
    () =>
      import('./screens/PresentationScreen/PresentationScreenContentWrapper')
  )
)

const PresentationEditScreen = wrapSuspenseScreen(
  loadable(
    () => import('./screens/PresentationEditScreen/PresentationEditScreen')
  )
)

const PresentationEditScreenContent = wrapSuspenseScreen(
  loadable(
    () =>
      import('./screens/PresentationEditScreen/PresentationEditScreenContent')
  )
)

const ClaimPresentationScreen = wrapSuspenseScreen(
  loadable(
    () => import('./screens/ClaimPresentationScreen/ClaimPresentationScreen')
  )
)

const ClaimPresentationDialogContent = wrapSuspenseComponent(
  loadable(() =>
    import('./screens/ClaimPresentationScreen/ClaimPresentationScreen').then(
      (m) => ({ default: m.ClaimPresentationDialogContent })
    )
  )
)

const ClaimEventHostDialogContent = wrapSuspenseComponent(
  loadable(() =>
    import('./screens/ClaimEventHostScreen/ClaimEventHostScreen').then(
      (m) => ({ default: m.ClaimEventHostDialogContent })
    )
  )
)

const PresentationSettingsNavigator = wrapSuspenseScreen(
  loadable(
    () => import('./navigators/PresentationSettingsNavigator')
  )
)

const EventNewScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/EventNewScreen/EventNewScreen'))
)

const PresentationNewScreen = wrapSuspenseScreen(
  loadable(
    () => import('./screens/PresentationNewScreen/PresentationNewScreen')
  )
)
const PresentationNewScreenContent = wrapSuspenseComponent(
  loadable(
    () => import('./screens/PresentationNewScreen/PresentationNewScreenContent')
  )
)

const EmbedNavigator = wrapSuspenseComponent(
  loadable(() => import('./navigators/EmbedNavigator'))
)

const EventNewContent = wrapSuspenseComponent(
  loadable(() => import('./screens/EventNewScreen/EventNewContent'))
)

const EventEditPresentationNewContent = wrapSuspenseComponent(
  loadable(
    () =>
      import(
        './screens/EventEditPresentationNewScreen/EventEditPresentationNewContent'
      )
  )
)

const SignUpScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/SignUpScreen/SignUpScreen'))
)
const SignUpDialog = loadable(
  () => import('./screens/SignUpScreen/SignUpDialog')
)

const SignInScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/SignInScreen/SignInScreen'))
)
const SignInDialog = loadable(
  () => import('./screens/SignInScreen/SignInDialog')
)

const AuthCodeScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/AuthCodeScreen/AuthCodeScreen'))
)

const AuthEmailActionScreen = wrapSuspenseScreen(
  loadable(
    () => import('./screens/AuthEmailActionScreen/AuthEmailActionScreen')
  )
)

const AuthCodeDialog = loadable(
  () => import('./screens/AuthCodeScreen/AuthCodeDialog')
)

const UnsubscribeScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/UnsubscribeScreen/UnsubscribeScreen'))
)

const DocsScreen = wrapSuspenseScreen(
  loadable(
    () => import('./screens/DocsScreen/DocsScreen')
  )
)

const TermsOfServiceScreen = wrapSuspenseScreen(
  loadable(
    () => import('./screens/TermsOfServiceScreen/TermsOfServiceScreen')
  )
)

const PrivacyPolicyScreen = wrapSuspenseScreen(
  loadable(
    () => import('./screens/PrivacyPolicyScreen/PrivacyPolicyScreen')
  )
)

const AdminNavigator = wrapSuspenseScreen(
  loadable(() => import('./navigators/AdminNavigator'))
)

const MaintenanceScreen = wrapSuspenseScreen(
  loadable(() => import('./screens/MaintenanceScreen/MaintenanceScreen'))
)

const DialogWrapper = wrapSuspenseComponent(
  loadable(() => import('./newComponents/Dialog/DialogWrapper'))
)

const GuildDonationDialog = loadable(
  () => import('./screens/GuildDonationScreen/GuildDonationDialog')
)

const GuildJoinConfirmationDialog = loadable(
  () =>
    import(
      './modals/GuildJoinConfirmation/GuildJoinConfirmationDialog'
    )
)

const EventAttendDialog = loadable(
  () => import('./screens/EventAttendScreen/EventAttendDialog')
)

const EventTicketPaymentDialog = loadable(
  () => import('./screens/EventTicketPaymentScreen/EventTicketPaymentDialog')
)

const OnboardingPhotoDialog = loadable(
  () => import('./screens/OnboardingScreen/OnboardingPhotoDialog')
)

const OnboardingRecommendationsDialog = loadable(
  () =>
    import(
      './screens/OnboardingScreen/OnboardingRecommendationsDialog'
    )
)

const createWildcardRedirect =
  (prefix: string): FC =>
  () => {
    const params = useParams()

    return (
      <Navigate
        to={[prefix, params['*']].filter(Boolean).join('/')}
        replace={true}
      />
    )
  }

const EventsRedirect = createWildcardRedirect('/events')
const PresentationsRedirect = createWildcardRedirect('/presentations')
const CivicTechTorontoRedirect = createWildcardRedirect('/civic-tech-toronto')

const THEME_COLORS = { dark: '#121212', light: '#F8F7FC' } as const

/**
 * Updates DOM state for theme switching. Sets color-scheme FIRST so Safari
 * sees a consistent state, then removes and re-inserts the theme-color meta
 * tag to force Safari to re-evaluate it (setAttribute alone doesn't trigger
 * Safari's theme-color engine reliably).
 */
function applyThemeToDom(theme: 'light' | 'dark') {
  const root = document.documentElement
  root.classList.remove('t_light', 't_dark')
  root.classList.add(theme === 'dark' ? 't_dark' : 't_light')
  // Set color-scheme BEFORE updating meta tag — Safari checks consistency
  root.style.colorScheme = theme
  // Remove and re-insert meta tag to force Safari to re-read theme-color
  const existing = document.getElementById('guild-theme-color')
  if (existing) existing.remove()
  const meta = document.createElement('meta')
  meta.name = 'theme-color'
  meta.id = 'guild-theme-color'
  meta.content = THEME_COLORS[theme]
  document.head.insertBefore(meta, document.head.firstChild)
}

const SentryRoutes =
  process.env.NODE_ENV === 'production'
    ? Sentry.withSentryReactRouterV6Routing(Routes)
    : Routes

export const App = ({
  relayEnvironment,
  accessToken,
  refreshToken,
  Router,
}: {
  relayEnvironment?: Environment
  accessToken?: string
  refreshToken?: string
  Router: ({
    children,
    onTransition,
  }: {
    children: React.ReactNode
    onTransition?: (transitioning: boolean) => any
  }) => JSX.Element
}) => {
  useTitle('Elevate Your Community | Guild')
  useMeta({ property: 'og:title', content: 'Elevate Your Community | Guild' })

  useMeta({
    name: 'description',
    content:
      'Check out Events and Presentations from your favorite communities on Guild.',
  })
  useMeta({
    property: 'og:description',
    content: 'Elevate your community',
  })

  const [isLoading, setIsLoading] = useState(false)
  const [themeName, setThemeName] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'light'
    try {
      const stored = localStorage.getItem('guild-theme')
      if (stored === 'dark' || stored === 'light') return stored
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    } catch {
      return 'light'
    }
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = (e: MediaQueryListEvent) => {
        // Only follow system preference if user hasn't set a manual preference
        try { if (localStorage.getItem('guild-theme')) return } catch {}
        const newTheme = e.matches ? 'dark' : 'light'
        setThemeName(newTheme)
        applyThemeToDom(newTheme)
      }
      mq.addEventListener('change', handler)
      return () => mq.removeEventListener('change', handler)
    } catch {}
  }, [])

  const applyTheme = useCallback((newTheme: 'light' | 'dark') => {
    setThemeName(newTheme)
    try { localStorage.setItem('guild-theme', newTheme) } catch {}
    applyThemeToDom(newTheme)
  }, [])

  const toggleTheme = useCallback(() => {
    applyTheme(themeName === 'dark' ? 'light' : 'dark')
  }, [themeName, applyTheme])

  const themeContextValue = useMemo(() => ({
    themeName,
    setThemeName: applyTheme,
    toggleTheme,
  }), [themeName, applyTheme, toggleTheme])

  const serverContext = useContext(ServerContext)

  const tamaguiConfigWithBreakpoints = useMemo(() => {
    if (!serverContext) {
      return tamaguiConfig
    }

    return {
      ...tamaguiConfig,
      mediaQueryDefaultActive: {
        ...tamaguiConfig.mediaQueryDefaultActive,
        xs: serverContext.defaultBreakpoint <= 0,
        gtXs: serverContext.defaultBreakpoint > 0,
        sm: serverContext.defaultBreakpoint <= 1,
        gtSm: serverContext.defaultBreakpoint > 1,
        md: serverContext.defaultBreakpoint <= 2,
        gtMd: serverContext.defaultBreakpoint > 2,
        lg: serverContext.defaultBreakpoint <= 3,
        gtLg: serverContext.defaultBreakpoint > 3,
        mdHt: serverContext.defaultBreakpoint <= 3,
        gtMdHt: serverContext.defaultBreakpoint > 3,
      },
    }
  }, [serverContext?.defaultBreakpoint])

  if (MAINTENANCE_MODE) {
    return (
      <I18nProvider>
        <Router
          onTransition={(transitioning) => {
            setIsLoading(transitioning)
          }}
        >
          <TamaguiProvider
            config={tamaguiConfigWithBreakpoints}
            disableInjectCSS={true}
            disableRootThemeClass={true}
            defaultTheme={themeName}
          >
            <ThemeContext.Provider value={themeContextValue}>
              <DynamicIslandProvider>
                <ErrorBoundary>
                  <AppContent isLoading={isLoading} />
                </ErrorBoundary>
              </DynamicIslandProvider>
            </ThemeContext.Provider>
          </TamaguiProvider>
        </Router>
      </I18nProvider>
    )
  }

  return (
    <DataProvider
      initialAccessToken={accessToken}
      initialRefreshToken={refreshToken}
      relayEnvironment={relayEnvironment}
    >
      <I18nProvider>
        <Router
          onTransition={(transitioning) => {
            setIsLoading(transitioning)
          }}
        >
          <TamaguiProvider
            config={tamaguiConfigWithBreakpoints}
            disableInjectCSS={true}
            disableRootThemeClass={true}
            defaultTheme={themeName}
          >
            <ThemeContext.Provider value={themeContextValue}>
              <DynamicIslandProvider>
                <SearchProvider>
                  <ErrorBoundary>
                    <AppContent isLoading={isLoading} />
                  </ErrorBoundary>
                </SearchProvider>
              </DynamicIslandProvider>
            </ThemeContext.Provider>
          </TamaguiProvider>
        </Router>
      </I18nProvider>
    </DataProvider>
  )
}

const AppContainer = ({
  children,
  isLoading = false,
}: {
  children: React.ReactNode
  isLoading?: boolean
}) => {
  return (
    <PaletteRootProvider>
      <DynamicIslandNew isLoading={isLoading} />

      {children}

      <Footer />
    </PaletteRootProvider>
  )
}

const AppContent = ({ isLoading = false }) => {
  const location = useLocation()
  const navigate = useNavigate()

  const locationState = location.state as {
    backgroundLocation?: Location
    displayLocation?: Location
  }

  // Onboarding and claim routes are dialog overlays that require a
  // backgroundLocation. When navigated to directly (e.g. pasting URL),
  // synthesise one so the dialog renders over the appropriate screen.
  const isOnboardingPath = location.pathname.startsWith('/onboarding/')
  const claimPathMatch = useMatch('/presentations/:presentationId/claim')
  const claimHostPathMatch = useMatch('/events/:eventId/claim-host')
  const isClaimPath = !!claimPathMatch || !!claimHostPathMatch
  const hasBackgroundLocation =
    !!locationState?.backgroundLocation || isOnboardingPath || isClaimPath

  const mainLocation: Location = useMemo(() => {
    // displayLocation overrides backgroundLocation for rendering — allows a
    // different screen to show behind the dialog while keeping the original
    // backgroundLocation as the dismissal target in DialogWrapper.
    // Strip backgroundLocation from displayLocation's state so child navigators
    // render the displayLocation's route directly.
    if (locationState?.displayLocation) {
      const {
        backgroundLocation: _bg,
        ...displayState
      } = (locationState.displayLocation.state || {}) as Record<string, unknown>
      return {
        ...locationState.displayLocation,
        state: {
          ...displayState,
          inModal: true,
        },
      }
    }

    if (locationState?.backgroundLocation) {
      return {
        ...locationState.backgroundLocation,
        state: {
          ...(locationState.backgroundLocation.state || {}),
          inModal: true,
        },
      }
    }

    if (isOnboardingPath) {
      return {
        pathname: '/',
        search: '',
        hash: '',
        state: { inModal: true },
        key: 'default',
      }
    }

    // Claim dialog: show the presentation page behind the claim dialog
    if (claimPathMatch?.params.presentationId) {
      return {
        pathname: `/presentations/${claimPathMatch.params.presentationId}`,
        search: '',
        hash: '',
        state: { inModal: true },
        key: 'claim-bg',
      }
    }

    // Claim-host dialog: show the event page behind the claim dialog
    if (claimHostPathMatch?.params.eventId) {
      return {
        pathname: `/events/${claimHostPathMatch.params.eventId}`,
        search: '',
        hash: '',
        state: { inModal: true },
        key: 'claim-host-bg',
      }
    }

    return { ...location, state: { ...(location.state || {}), inModal: false } }
  }, [locationState, location, isOnboardingPath, isClaimPath, claimPathMatch])

  if (MAINTENANCE_MODE) {
    return <MaintenanceScreen />
  }

  return (
    <SentryRoutes>
      <Route path='/embeds/*' element={<EmbedNavigator />} />

      <Route
        path='*'
        element={
          <AppContainer isLoading={isLoading}>
            <Routes location={mainLocation}>
              <Route path='/' element={<HomeScreen />} />
              <Route path='/guilds/*' element={<GuildsScreenNew />} />
              <Route path='/guilds/new' element={<GuildNewScreen />} />

              <Route path='/e/*' element={<EventsRedirect />} />

              <Route path='/events' element={<EventsScreen />} />
              <Route path='/events/new' element={<EventNewScreen />} />
              <Route
                path='/events/:eventId/*'
                element={<WrappedEventNavigator />}
              />

              <Route path='/p/*' element={<PresentationsRedirect />} />

              <Route path='/presentations' element={<PresentationsScreen />} />

              <Route
                path='/integrations/meetup'
                element={<MeetupIntegrationScreen />}
              />

              <Route
                path='/integrations/bluesky'
                element={<BlueskyIntegrationScreen />}
              />

              <Route
                path='/connect/google/callback'
                element={<GoogleCalendarConnectScreen />}
              />

              <Route
                path='/presentations/new'
                element={<PresentationNewScreen />}
              />
              <Route
                path='/presentations/:presentationId'
                element={<PresentationScreen />}
              />
              <Route
                path='/presentations/:presentationId/settings/*'
                element={<PresentationSettingsNavigator />}
              />
              <Route
                path='/presentations/:presentationId/claim'
                element={<ClaimPresentationScreen />}
              />
              <Route
                path='/presentations/:presentationId/edit'
                element={<PresentationEditScreen />}
              />

              <Route path='/sign-in' element={<SignInScreen />} />
              <Route path='/sign-up' element={<SignUpScreen />} />

              <Route path='/auth/code' element={<AuthCodeScreen />} />
              <Route
                path='/auth/email-action'
                element={<AuthEmailActionScreen />}
              />

              <Route path='/unsubscribe' element={<UnsubscribeScreen />} />

              <Route path='/docs/*' element={<DocsScreen />} />

              <Route path='/legal/terms' element={<TermsOfServiceScreen />} />
              <Route path='/legal/privacy' element={<PrivacyPolicyScreen />} />

              <Route path='/admin/*' element={<AdminNavigator />} />

              <Route
                path='/oauth/authorize'
                element={<OAuthAuthorizeScreen />}
              />

              {/* Can remove April 1, 2026 */}
              <Route path='/ctto/*' element={<CivicTechTorontoRedirect />} />

              <Route path='/:actorId/*' element={<WrappedActorNavigator />} />
            </Routes>

            {hasBackgroundLocation && (
              <Routes>
                <Route
                  path='/presentations/new'
                  element={
                    <DialogWrapper>
                      <PresentationNewScreenContent />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/presentations/:presentationId'
                  element={
                    <DialogWrapper contentProps={{ padding: 0 }}>
                      <PresentationScreenContentWrapper />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/presentations/:presentationId/edit'
                  element={
                    <DialogWrapper>
                      <PresentationEditScreenContent />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/presentations/:presentationId/claim'
                  element={
                    <DialogWrapper
                      contentProps={{
                        maxWidth: 500,
                      }}
                    >
                      <ClaimPresentationDialogContent />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/events/:eventId/claim-host'
                  element={
                    <DialogWrapper
                      contentProps={{
                        maxWidth: 500,
                      }}
                    >
                      <ClaimEventHostDialogContent />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/events/new'
                  element={
                    <DialogWrapper>
                      <EventNewContent />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/guilds/new'
                  element={
                    <DialogWrapper>
                      <GuildNewContent />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/events/:eventId/settings/presentations/new'
                  element={
                    <DialogWrapper>
                      <EventEditPresentationNewContent
                        onCreate={() => navigate(-1)}
                      />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/events/:eventId/attend'
                  element={
                    <DialogWrapper>
                      <EventAttendDialog />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/events/:eventId/payment'
                  element={
                    <DialogWrapper
                      contentProps={{
                        $gtXs: { width: 536 },
                        $gtSm: { minWidth: 600 },
                      }}
                    >
                      <EventTicketPaymentDialog />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/sign-in'
                  element={
                    <DialogWrapper
                      contentProps={{
                        padding: 0,
                        width: 400,
                        maxWidth: 400,
                        minHeight: 300,
                      }}
                      sheetScrollViewProps={{
                        padding: 0,
                      }}
                    >
                      <SignInDialog />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/sign-up'
                  element={
                    <DialogWrapper
                      contentProps={{
                        padding: 0,
                        width: 400,
                        maxWidth: 400,
                        minHeight: 300,
                      }}
                      sheetScrollViewProps={{
                        padding: 0,
                      }}
                    >
                      <SignUpDialog />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/auth/code'
                  element={
                    <DialogWrapper
                      contentProps={{ minHeight: 300, padding: 0 }}
                    >
                      <AuthCodeDialog />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/onboarding/photo'
                  element={
                    <DialogWrapper
                      contentProps={{
                        padding: 0,
                        width: 450,
                        maxWidth: 450,
                        minHeight: 300,
                      }}
                    >
                      <OnboardingPhotoDialog />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/onboarding/recommendations'
                  element={
                    <DialogWrapper
                      contentProps={{
                        padding: 0,
                        width: 550,
                        maxWidth: 550,
                        minHeight: 300,
                      }}
                    >
                      <OnboardingRecommendationsDialog />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/:actorId/donate'
                  element={
                    <DialogWrapper>
                      <GuildDonationDialog />
                    </DialogWrapper>
                  }
                />

                <Route
                  path='/:actorId/confirm'
                  element={
                    <DialogWrapper contentProps={{ maxWidth: 600 }}>
                      <GuildJoinConfirmationDialog />
                    </DialogWrapper>
                  }
                />
              </Routes>
            )}
          </AppContainer>
        }
      />
    </SentryRoutes>
  )
}

export default App
